import React from 'react';
import { Box, CssBaseline } from '@mui/material';
import { BrowserRouter, MemoryRouter, Routes, Route, Navigate } from 'react-router-dom';
import Header from './components/Header';
import OnboardingGate from './components/OnboardingGate';
import AutoCompleteDialog from './components/dialogs/AutoCompleteDialog';
import LoginPage from './components/auth/LoginPage';
import AllArticlesPage from './pages/AllArticlesPage';
import SettingsPage from './pages/SettingsPage';
import SourcesSettingsPage from './pages/SourcesSettingsPage';
import { SourcesProvider, useSources } from './context/SourcesContext';
import { DialogProvider } from './context/DialogContext';
import { ToastProvider } from './context/ToastContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { isEmbedded, getEmbedView } from './lib/embedMode';

function App() {
  return (
    <AuthProvider>
      <CssBaseline />
      <AppContent />
    </AuthProvider>
  );
}

const AppContent: React.FC = () => {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  const routedContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* The app's own AppBar duplicates CC's page chrome and breadcrumbs
      when rendered inside the ComplyControl iframe, so it must not render
      there (COM-636). The onboarding auto-open that used to live inside
      Header/UserProfile moves to OnboardingGate below, which renders in
      every mode. */}
      {!isEmbedded() && <Header />}

      <Routes>
        {/* COM-638: the All Articles feed replaces the old sources/content-area
        two-pane layout as the app's main screen. */}
        <Route path="/" element={<AllArticlesPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        {/* COM-647 Task 4: Settings / Regulations Scanner / Sources (Figma
        1883:10111). A sibling of `/settings`, not nested under it - `/settings`
        is the project questionnaire (COM-637/638), and stays that way: the
        design spec deliberately does NOT extend the embed `view` list or repurpose
        that route for this page. Reached only from the "Sources Settings" button
        in the All Articles page's own Sources filter dropdown
        (SourcesFilter.tsx), never a deep link from ComplyControl (out of scope,
        see the design spec). */}
        <Route path="/sources" element={<SourcesSettingsPage />} />
        {/* Any unrecognised path (typo'd URL, stale bookmark) previously
        rendered a blank body under the Header — send it back to the main
        view instead of leaving the user stranded. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Box>
  );

  return (
    // COM-647 Task 3: `ToastProvider` wraps `SourcesProvider`, not the other
    // way round - `SourcesProvider` itself now calls `useToast()` to raise
    // its socket-level 'error' events (folded in from its own former
    // Snackbar/Alert, see SourcesContext.tsx's comment on that), so it needs
    // a `ToastProvider` ancestor to render under at all.
    <ToastProvider>
      <SourcesProvider>
        <DialogProvider>
          {/* Embedded mode is served from POST /embed/bootstrap (COM-636): the iframe's
          URL is not a route the backend serves, so pushing history entries there
          would break the handshake on reload. MemoryRouter keeps navigation entirely
          in memory. `getEmbedView()` picks the landing route — that is a choice of
          starting page, not a permission: both routes stay reachable once inside. */}
          {isEmbedded() ? (
            <MemoryRouter initialEntries={[getEmbedView() === 'settings' ? '/settings' : '/']}>
              {routedContent}
            </MemoryRouter>
          ) : (
            <BrowserRouter>{routedContent}</BrowserRouter>
          )}

          <OnboardingGate />
          <AutoCompleteDialogWrapper />
        </DialogProvider>
      </SourcesProvider>
    </ToastProvider>
  );
};

// Wrapper component to access context
const AutoCompleteDialogWrapper: React.FC = () => {
  const { isAutoCompleteInProgress } = useSources();
  
  return <AutoCompleteDialog open={isAutoCompleteInProgress} />;
};

export default App; 